import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CompaniesService } from './companies.service';
import { UserService } from './user.service';
import { UserCompany } from '../models/user/user-companies-response-dto.model';

export type SearchResultType =
  | 'individual'
  | 'business'
  | 'introducer'
  | 'invoice'
  | 'quotation'
  | 'credit-note'
  | 'receipt'
  | 'vendor'
  | 'document'
  | 'task'
  | 'meeting-note'
  | 'reference-number';

export interface GlobalSearchResult {
  id: number | string;
  type: SearchResultType;
  category: string;
  title: string;
  subtitle: string;
  icon: string;
  route: string;
  queryParams?: Record<string, any>;
  refCode?: string;  // Reference number/code for this item
  clientData?: { clientId: number; client_name: string; client_code: string };
  companyId?: number;
  companyName?: string;
}

export interface GroupedSearchResults {
  category: string;
  results: GlobalSearchResult[];
}

interface CompanyCache {
  customerMap: Map<number, string>;
  formsCache: any[];
}

@Injectable({
  providedIn: 'root'
})
export class GlobalSearchService {
  private readonly corporateUrl = environment.apiConfig.corporateAPI.endpoint;
  private readonly invoicingUrl = environment.invoicing_api;
  private readonly httpClient = inject(HttpClient);
  private readonly companiesService = inject(CompaniesService);
  private readonly userService = inject(UserService);

  // Per-company customer/forms caches keyed by companyId
  private readonly caches = new Map<number, CompanyCache>();

  /**
   * Search across the user's accessible companies.
   * @param query Search query string (minimum 2 characters)
   * @param filters Optional array of category filter values to limit search scope
   * @param companyIds Optional list of company IDs to scope the search to (empty/undefined = all)
   * @returns Observable of grouped search results
   */
  search(
    query: string,
    filters: string[] = [],
    companyIds: number[] = []
  ): Observable<GroupedSearchResults[]> {
    if (!query || query.trim().length < 2) {
      return of([]);
    }

    const searchTerm = query.trim().toLowerCase();
    const searchByRefOnly = filters.includes('refno') && filters.length === 1;

    return this.resolveCompanies(companyIds).pipe(
      switchMap(companies => {
        if (!companies.length) {
          return of([] as GroupedSearchResults[]);
        }
        const perCompany = companies.map(company =>
          this.searchSingleCompany(company, searchTerm, filters)
        );
        return forkJoin(perCompany).pipe(
          map(results => results.flat()),
          map(allResults => this.groupResults(allResults, searchByRefOnly, searchTerm))
        );
      })
    );
  }

  /**
   * Resolve the list of companies the user can search across.
   * Prefers the cached BehaviorSubject; falls back to a fresh fetch when empty.
   * If `companyIds` is non-empty, the result is filtered to only those companies the user has access to.
   */
  private resolveCompanies(companyIds: number[] = []): Observable<UserCompany[]> {
    return this.userService.getUserCompaniesAsObs().pipe(
      take(1),
      switchMap(companies => {
        if (companies && companies.length > 0) {
          return of(companies);
        }
        return this.userService.getUserCompanies().pipe(
          map(res => res?.companies || []),
          catchError(() => of([] as UserCompany[]))
        );
      }),
      map(companies => {
        let resolved: UserCompany[];
        if (companies && companies.length > 0) {
          resolved = companies;
        } else {
          // Fallback: at least search the currently managed company
          const currentId = this.companiesService.managedCompanyId;
          resolved = currentId ? [{ id: currentId, name: '' } as UserCompany] : [];
        }
        if (companyIds && companyIds.length > 0) {
          const allowed = new Set(companyIds);
          return resolved.filter(c => allowed.has(c.id));
        }
        return resolved;
      })
    );
  }

  private getCache(companyId: number): CompanyCache {
    let cache = this.caches.get(companyId);
    if (!cache) {
      cache = { customerMap: new Map<number, string>(), formsCache: [] };
      this.caches.set(companyId, cache);
    }
    return cache;
  }

  private searchSingleCompany(
    company: UserCompany,
    searchTerm: string,
    filters: string[]
  ): Observable<GlobalSearchResult[]> {
    const shouldSearchClients = filters.length === 0 || filters.includes('clients') || filters.includes('refno');
    const shouldSearchInvoicing = filters.length === 0 || filters.includes('invoicing') || filters.includes('refno');
    const shouldSearchDocuments = filters.length === 0 || filters.includes('documents') || filters.includes('refno');
    const shouldSearchTasks = filters.length === 0 || filters.includes('tasks');
    const shouldSearchRefNumbers = filters.length === 0 || filters.includes('refno');

    return this.buildCustomerMap(company.id).pipe(
      switchMap(() => forkJoin({
        forms: shouldSearchClients ? this.searchForms(company, searchTerm) : of([]),
        introducers: shouldSearchClients ? this.searchIntroducers(company, searchTerm) : of([]),
        invoices: shouldSearchInvoicing ? this.searchInvoices(company, searchTerm) : of([]),
        quotations: shouldSearchInvoicing ? this.searchQuotations(company, searchTerm) : of([]),
        creditNotes: shouldSearchInvoicing ? this.searchCreditNotes(company, searchTerm) : of([]),
        receipts: shouldSearchInvoicing ? this.searchReceipts(company, searchTerm) : of([]),
        vendors: shouldSearchInvoicing ? this.searchVendors(company, searchTerm) : of([]),
        documents: shouldSearchDocuments ? this.searchDocuments(company, searchTerm) : of([]),
        tasks: shouldSearchTasks ? this.searchTasks(company, searchTerm) : of([]),
        referenceNumbers: shouldSearchRefNumbers ? this.searchReferenceNumbers(company, searchTerm) : of([]),
      })),
      map(parts => [
        ...parts.forms,
        ...parts.introducers,
        ...parts.invoices,
        ...parts.quotations,
        ...parts.creditNotes,
        ...parts.receipts,
        ...parts.vendors,
        ...parts.documents,
        ...parts.tasks,
        ...parts.referenceNumbers,
      ]),
      catchError(() => of([] as GlobalSearchResult[]))
    );
  }

  private searchReferenceNumbers(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '10')
      .set('search', query);

    return this.httpClient.get<any>(
      `${this.corporateUrl}/api/companies/${company.id}/reference-numbers`,
      { params }
    ).pipe(
      map(response => {
        const items = response?.items || [];
        return items.slice(0, 10).map((ref: any) => {
          const yearLabel = ref.service?.year ? ` - ${ref.service.year}` : '';
          const clientLabel = ref.clientName || 'No client';
          return this.withCompany({
            id: ref.referenceNumber,
            type: 'reference-number',
            category: 'Reference Numbers',
            title: ref.referenceNumber || 'No number',
            subtitle: `Reference - ${clientLabel}${yearLabel}`,
            icon: 'tag',
            route: '/reference-numbers',
            queryParams: { search: ref.referenceNumber },
            refCode: ref.referenceNumber || ''
          } as GlobalSearchResult, company);
        });
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Build customer lookup map from forms (individuals + businesses) for the given company.
   */
  private buildCustomerMap(companyId: number): Observable<void> {
    const cache = this.getCache(companyId);
    if (cache.customerMap.size > 0) {
      return of(undefined);
    }

    return forkJoin({
      individuals: this.httpClient.post<any>(
        `${this.corporateUrl}/company/${companyId}/forms`,
        { pageNumber: 1, pageSize: 1000 },
        { params: new HttpParams().set('formType', '0') }
      ).pipe(
        map(res => res?.items || []),
        catchError(() => of([]))
      ),
      businesses: this.httpClient.post<any>(
        `${this.corporateUrl}/company/${companyId}/forms`,
        { pageNumber: 1, pageSize: 1000 },
        { params: new HttpParams().set('formType', '1') }
      ).pipe(
        map(res => res?.items || []),
        catchError(() => of([]))
      )
    }).pipe(
      map(({ individuals, businesses }) => {
        cache.customerMap.clear();
        const allForms = [...individuals, ...businesses];
        cache.formsCache = allForms;
        allForms.forEach((form: any) => {
          if (form.id && form.displayName) {
            cache.customerMap.set(form.id, form.displayName);
          }
        });
      }),
      catchError(() => of(undefined))
    );
  }

  private getCustomerName(companyId: number, customerId: number): string {
    return this.getCache(companyId).customerMap.get(customerId) || 'Unknown';
  }

  private withCompany(result: GlobalSearchResult, company: UserCompany): GlobalSearchResult {
    const companyName = company.name || '';
    return {
      ...result,
      companyId: company.id,
      companyName,
      subtitle: companyName ? `${result.subtitle} - ${companyName}` : result.subtitle
    };
  }

  private searchForms(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const statusIds = [0, 1, 2, 3];
    const cache = this.getCache(company.id);

    const searches = statusIds.map(statusId => {
      const params = new HttpParams()
        .set('searchString', query)
        .set('clientStatus', statusId.toString());

      return this.httpClient.post<any>(
        `${this.corporateUrl}/company/${company.id}/forms`,
        { pageNumber: 1, pageSize: 10 },
        { params }
      ).pipe(
        map(response => response?.items || []),
        catchError(() => of([]))
      );
    });

    return forkJoin(searches).pipe(
      map(results => {
        const allItems = results.flat();
        const cachedMatches = cache.formsCache.filter((form: any) => {
          const displayName = (form.displayName || '').toLowerCase();
          const name = (form.name || '').toLowerCase();
          const code = (form.code || '').toLowerCase();
          return displayName.includes(query) || name.includes(query) || code.includes(query);
        });
        const merged = [...allItems, ...cachedMatches];
        const uniqueItems = this.removeDuplicates(merged, 'id');

        return uniqueItems.slice(0, 15).map((form: any) => {
          const isIndividual = form.type === 0;
          const statusLabel = this.getClientStatusLabel(form.clientStatus);
          return this.withCompany({
            id: form.id,
            type: isIndividual ? 'individual' : 'business',
            category: 'Clients',
            title: form.displayName || form.name || 'Unknown',
            subtitle: `${isIndividual ? 'Individual' : 'Business'} - ${form.code || 'No code'} - ${statusLabel}`,
            icon: isIndividual ? 'person' : 'business',
            route: `/edit-form/${form.id}`,
            refCode: form.code || ''
          } as GlobalSearchResult, company);
        });
      }),
      catchError(() => of([]))
    );
  }

  private removeDuplicates<T>(array: T[], key: keyof T): T[] {
    const seen = new Set();
    return array.filter(item => {
      const value = item[key];
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  }

  private getClientStatusLabel(statusId: number): string {
    const statuses: { [key: number]: string } = {
      0: 'Active',
      1: 'Non Active',
      2: 'N/A',
      3: 'Organization'
    };
    return statuses[statusId] || 'Unknown';
  }

  private searchIntroducers(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('pageIndex', '0')
      .set('pageSize', '10')
      .set('query', query);

    return this.httpClient.get<any[]>(
      `${this.corporateUrl}/company/${company.id}/introducers`,
      { params }
    ).pipe(
      map(introducers => {
        return (introducers || []).map(intro => this.withCompany({
          id: intro.id,
          type: 'introducer',
          category: 'Clients',
          title: intro.name || 'Unknown',
          subtitle: `Introducer - ${intro.code || 'No code'}`,
          icon: 'supervisor_account',
          route: `/edit-introducer/${intro.id}`
        } as GlobalSearchResult, company));
      }),
      catchError(() => of([]))
    );
  }

  private searchInvoices(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '1000');
    const cache = this.getCache(company.id);

    return this.httpClient.get<any>(
      `${this.invoicingUrl}companies/${company.id}/invoices`,
      { params }
    ).pipe(
      map(response => {
        const items = response?.items || [];
        return items
          .filter((inv: any) =>
            (inv.invoiceNumber?.toLowerCase().includes(query)) ||
            (this.getCustomerName(company.id, inv.customerId).toLowerCase().includes(query))
          )
          .slice(0, 5)
          .map((inv: any) => {
            const form = cache.formsCache.find((f: any) => f.id === inv.customerId);
            return this.withCompany({
              id: inv.id,
              type: 'invoice',
              category: 'Invoicing',
              title: inv.invoiceNumber || 'No number',
              subtitle: `Invoice - ${this.getCustomerName(company.id, inv.customerId)} - ${this.formatCurrency(inv.totalAmount)}`,
              icon: 'receipt_long',
              route: '/invoicing/invoices-new/create',
              queryParams: { invoiceId: inv.id, preview: true },
              refCode: inv.invoiceNumber || '',
              clientData: form ? { clientId: inv.customerId, client_name: form.displayName || '', client_code: form.code || '' } : undefined
            } as GlobalSearchResult, company);
          });
      }),
      catchError(() => of([]))
    );
  }

  private searchQuotations(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '1000');
    const cache = this.getCache(company.id);

    return this.httpClient.get<any>(
      `${this.invoicingUrl}companies/${company.id}/quotations`,
      { params }
    ).pipe(
      map(response => {
        const items = response?.items || [];
        return items
          .filter((quot: any) =>
            (quot.quotationNumber?.toLowerCase().includes(query)) ||
            (this.getCustomerName(company.id, quot.customerId).toLowerCase().includes(query))
          )
          .slice(0, 5)
          .map((quot: any) => {
            const form = cache.formsCache.find((f: any) => f.id === quot.customerId);
            return this.withCompany({
              id: quot.id,
              type: 'quotation',
              category: 'Invoicing',
              title: quot.quotationNumber || 'No number',
              subtitle: `Quotation - ${this.getCustomerName(company.id, quot.customerId)} - ${this.formatCurrency(quot.totalAmount)}`,
              icon: 'request_quote',
              route: '/invoicing/quotations/create',
              queryParams: { quotationId: quot.id, preview: true },
              refCode: quot.quotationNumber || '',
              clientData: form ? { clientId: quot.customerId, client_name: form.displayName || '', client_code: form.code || '' } : undefined
            } as GlobalSearchResult, company);
          });
      }),
      catchError(() => of([]))
    );
  }

  private searchCreditNotes(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '1000');
    const cache = this.getCache(company.id);

    return this.httpClient.get<any>(
      `${this.invoicingUrl}companies/${company.id}/creditnotes`,
      { params }
    ).pipe(
      map(response => {
        const items = response?.items || [];
        return items
          .filter((cn: any) =>
            (cn.creditNoteNumber?.toLowerCase().includes(query)) ||
            (this.getCustomerName(company.id, cn.customerId).toLowerCase().includes(query))
          )
          .slice(0, 5)
          .map((cn: any) => {
            const form = cache.formsCache.find((f: any) => f.id === cn.customerId);
            return this.withCompany({
              id: cn.id,
              type: 'credit-note',
              category: 'Invoicing',
              title: cn.creditNoteNumber || 'No number',
              subtitle: `Credit Note - ${this.getCustomerName(company.id, cn.customerId)} - ${this.formatCurrency(cn.totalAmount)}`,
              icon: 'money_off',
              route: '/invoicing/credit-notes/create',
              queryParams: { creditNoteId: cn.id, preview: true },
              refCode: cn.creditNoteNumber || '',
              clientData: form ? { clientId: cn.customerId, client_name: form.displayName || '', client_code: form.code || '' } : undefined
            } as GlobalSearchResult, company);
          });
      }),
      catchError(() => of([]))
    );
  }

  private searchReceipts(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('pageNumber', '1')
      .set('pageSize', '1000');
    const cache = this.getCache(company.id);

    return this.httpClient.get<any>(
      `${this.invoicingUrl}companies/${company.id}/receipts`,
      { params }
    ).pipe(
      map(response => {
        const items = response?.items || [];
        return items
          .filter((rec: any) =>
            (rec.receiptNumber?.toLowerCase().includes(query)) ||
            (this.getCustomerName(company.id, rec.customerId).toLowerCase().includes(query))
          )
          .slice(0, 5)
          .map((rec: any) => {
            const form = cache.formsCache.find((f: any) => f.id === rec.customerId);
            return this.withCompany({
              id: rec.id,
              type: 'receipt',
              category: 'Invoicing',
              title: rec.receiptNumber || 'No number',
              subtitle: `Receipt - ${this.getCustomerName(company.id, rec.customerId)} - ${this.formatCurrency(rec.totalAmount)}`,
              icon: 'receipt',
              route: '/invoicing/receipts/create',
              queryParams: { receiptId: rec.id, preview: true },
              refCode: rec.receiptNumber || '',
              clientData: form ? { clientId: rec.customerId, client_name: form.displayName || '', client_code: form.code || '' } : undefined
            } as GlobalSearchResult, company);
          });
      }),
      catchError(() => of([]))
    );
  }

  private searchVendors(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    const params = new HttpParams()
      .set('take', '10')
      .set('skip', '0')
      .set('search', query);

    return this.httpClient.get<any[]>(
      `${this.corporateUrl}/company/${company.id}/vendors`,
      { params }
    ).pipe(
      map(vendors => {
        return (vendors || []).slice(0, 5).map(vendor => this.withCompany({
          id: vendor.vendorId,
          type: 'vendor',
          category: 'Invoicing',
          title: vendor.vendorName || 'Unknown',
          subtitle: `Vendor - ${vendor.vendorCode || 'No code'}`,
          icon: 'store',
          route: '/invoicing/vendors/create',
          queryParams: { id: vendor.vendorId },
          refCode: vendor.vendorCode || ''
        } as GlobalSearchResult, company));
      }),
      catchError(() => of([]))
    );
  }

  private searchDocuments(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    return this.httpClient.post<any>(
      `${this.corporateUrl}/api/companies/${company.id}/dms/search`,
      { pageNumber: 1, pageSize: 10 },
      { params: new HttpParams().set('search', query) }
    ).pipe(
      map(response => {
        const items = response?.items || [];
        return items.slice(0, 5).map((doc: any) => this.withCompany({
          id: doc.id,
          type: 'document',
          category: 'Documents',
          title: doc.description || doc.fileName || 'Unnamed document',
          subtitle: `Document - ${doc.acr || ''} ${doc.code || ''}`.trim(),
          icon: 'description',
          route: '/file-system',
          queryParams: { search: doc.description || doc.fileName },
          refCode: doc.code || doc.acr || ''
        } as GlobalSearchResult, company));
      }),
      catchError(() => of([]))
    );
  }

  private searchTasks(company: UserCompany, query: string): Observable<GlobalSearchResult[]> {
    return this.httpClient.get<any[]>(
      `${this.corporateUrl}/api/companies/${company.id}/planner/tasks`
    ).pipe(
      map(tasks => {
        return (tasks || [])
          .filter(task =>
            (task.title?.toLowerCase().includes(query)) ||
            (task.description?.toLowerCase().includes(query))
          )
          .slice(0, 5)
          .map(task => this.withCompany({
            id: task.id,
            type: 'task',
            category: 'Tasks',
            title: task.title || 'Untitled task',
            subtitle: `Task - ${task.bucketName || 'No bucket'}`,
            icon: 'task_alt',
            route: '/task-planner',
            queryParams: { taskId: task.id }
          } as GlobalSearchResult, company));
      }),
      catchError(() => of([]))
    );
  }

  private groupResults(
    allResults: GlobalSearchResult[],
    searchByRefOnly: boolean = false,
    searchTerm: string = ''
  ): GroupedSearchResults[] {
    // Real reference number results from the API (already categorized).
    const realRefNumberResults = allResults.filter(r => r.category === 'Reference Numbers');

    // Synthetic cross-references: client matches surfaced under "Reference Numbers" too.
    const syntheticRefNumberResults: GlobalSearchResult[] = [];
    if (searchTerm) {
      const clientResults = allResults.filter(r => r.category === 'Clients');
      clientResults.forEach(result => {
        const matchesTitle = result.title?.toLowerCase().includes(searchTerm);
        const matchesSubtitle = result.subtitle?.toLowerCase().includes(searchTerm);
        const matchesRefCode = result.refCode?.toLowerCase().includes(searchTerm);
        if (matchesTitle || matchesSubtitle || matchesRefCode) {
          syntheticRefNumberResults.push({
            ...result,
            category: 'Reference Numbers',
            route: '/reference-numbers',
            queryParams: { search: searchTerm },
            icon: 'tag'
          });
        }
      });
    }

    const refNumberResults = [...realRefNumberResults, ...syntheticRefNumberResults];

    if (searchByRefOnly && searchTerm) {
      if (refNumberResults.length > 0) {
        return [{
          category: 'Reference Numbers',
          results: refNumberResults.slice(0, 10)
        }];
      }
      return [];
    }

    const grouped = new Map<string, GlobalSearchResult[]>();
    if (refNumberResults.length > 0) {
      grouped.set('Reference Numbers', refNumberResults);
    }
    allResults.forEach(result => {
      // Real reference-number results were already added above; skip them to avoid double-counting.
      if (result.category === 'Reference Numbers') return;
      const existing = grouped.get(result.category) || [];
      existing.push(result);
      grouped.set(result.category, existing);
    });

    const categoryOrder = ['Clients', 'Reference Numbers', 'Invoicing', 'Documents', 'Tasks'];
    const currentCompanyId = this.companiesService.managedCompanyId;

    return categoryOrder
      .filter(cat => grouped.has(cat))
      .map(cat => {
        const items = grouped.get(cat)!;
        // Sort: results from current company first, then by company name, then by title
        items.sort((a, b) => {
          const aCurrent = a.companyId === currentCompanyId ? 0 : 1;
          const bCurrent = b.companyId === currentCompanyId ? 0 : 1;
          if (aCurrent !== bCurrent) return aCurrent - bCurrent;
          const companyCompare = (a.companyName || '').localeCompare(b.companyName || '');
          if (companyCompare !== 0) return companyCompare;
          return (a.title || '').localeCompare(b.title || '');
        });
        return {
          category: cat,
          results: items.slice(0, 10)
        };
      });
  }

  private formatCurrency(amount: number): string {
    if (amount === null || amount === undefined) return '';
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  }
}
